import type { DocumentTemplate } from '@/entities/templates';
import type { EncounterSchema } from '@/entities/templates';

// The proposal preview is model-generated JSON, so field keys are not
// guaranteed to be lowercase: proto-typed section specs marshal with
// capitalized Go field names ("Title", "TemplateType") while the top-level
// change struct carries its own json tags ("base_series_id", "tab_title").
// Parsing therefore reads keys case-insensitively and normalizes everything
// into the shapes below.

export type ProposalSection = {
  code: string;
  title: string;
  category?: string;
  instructions?: string;
  templateType?: string;
  processingType?: string;
  required?: boolean;
};

// Matching key for diffs: the model may omit section codes, so the title
// doubles as the identity. Compared case- and whitespace-insensitively.
// Separator-insensitive: the model may echo a code as "visit_type",
// "visit-type" or "visit type" — all must match the stored section code.
const sectionKey = (section: ProposalSection): string =>
  (section.code || section.title)
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, ' ');

export type ProposalMeta = {
  name?: string;
  tabTitle?: string;
  description?: string;
  type?: string;
  language?: string;
};

export type ProposalDocumentRef = {
  seriesID: string;
  inclusion?: string;
};

export type ParsedTemplateProposal = {
  kind: 'template';
  baseSeriesID?: string;
  meta: ProposalMeta;
  sections: ProposalSection[];
};

export type ParsedSchemaProposal = {
  kind: 'schema';
  schemaID?: string;
  meta: ProposalMeta;
  documents: ProposalDocumentRef[];
};

export type ParsedProposal = ParsedTemplateProposal | ParsedSchemaProposal;

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

// Case-insensitive, separator-insensitive key lookup over one record:
// "base_series_id" matches "BaseSeriesID".
const normalizeKey = (key: string) => key.toLowerCase().replace(/_/g, '');

const pick = (record: Record<string, unknown>, key: string): unknown => {
  const normalized = normalizeKey(key);
  for (const [candidate, value] of Object.entries(record)) {
    if (normalizeKey(candidate) === normalized) {
      return value;
    }
  }
  return undefined;
};

const pickString = (record: Record<string, unknown>, key: string) => {
  const value = pick(record, key);
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
};

const pickBoolean = (record: Record<string, unknown>, key: string) => {
  const value = pick(record, key);
  return typeof value === 'boolean' ? value : undefined;
};

const parseSection = (value: unknown): ProposalSection | null => {
  const record = asRecord(value);
  // The model may omit the section code entirely; the title is then the only
  // stable identity, so it doubles as the code.
  const code = pickString(record, 'code') ?? pickString(record, 'title');
  if (!code) {
    return null;
  }
  const title = pickString(record, 'title');
  return {
    code: code ?? '',
    title: title ?? code ?? '',
    category: pickString(record, 'category'),
    instructions: pickString(record, 'instructions'),
    templateType: pickString(record, 'template_type'),
    processingType: pickString(record, 'processing_type'),
    required: pickBoolean(record, 'required'),
  };
};

export const parseProposal = (
  kind: string,
  preview: Record<string, unknown>,
): ParsedProposal => {
  if (kind === 'schema') {
    const documents = Array.isArray(pick(preview, 'documents'))
      ? (pick(preview, 'documents') as unknown[])
      : [];
    return {
      kind: 'schema',
      schemaID: pickString(preview, 'schema_id'),
      meta: {
        name: pickString(preview, 'name'),
        description: pickString(preview, 'description'),
        language: pickString(preview, 'language'),
      },
      documents: documents
        .map((document): ProposalDocumentRef | null => {
          const record = asRecord(document);
          const seriesID = pickString(record, 'template_series_id');
          const code = pickString(record, 'code');
          if (!seriesID && !code) {
            return null;
          }
          return {
            seriesID: seriesID ?? code ?? '',
            inclusion: pickString(record, 'inclusion'),
          };
        })
        .filter(
          (document): document is ProposalDocumentRef => document !== null,
        ),
    };
  }

  const sections = Array.isArray(pick(preview, 'sections'))
    ? (pick(preview, 'sections') as unknown[])
    : [];
  return {
    kind: 'template',
    baseSeriesID: pickString(preview, 'base_series_id'),
    meta: {
      name: pickString(preview, 'name'),
      tabTitle: pickString(preview, 'tab_title'),
      description: pickString(preview, 'description'),
      type: pickString(preview, 'type'),
      language: pickString(preview, 'language'),
    },
    sections: sections
      .map(parseSection)
      .filter((section): section is ProposalSection => section !== null),
  };
};

////////////////////////////////////////////////////////////////
//// DIFFS

export type ProposalField =
  | 'name'
  | 'tabTitle'
  | 'description'
  | 'type'
  | 'language';

export type MetaFieldChange = {
  field: ProposalField;
  from?: string;
  to?: string;
};

export type SectionField =
  | 'title'
  | 'category'
  | 'instructions'
  | 'templateType'
  | 'processingType'
  | 'required';

export type SectionFieldChange = {
  field: SectionField;
  // Raw values: strings are shown verbatim; booleans are localized at
  // render time (the lib has no access to intl).
  from?: string | boolean;
  to?: string | boolean;
};

export type TemplateSectionDiff = {
  status: 'added' | 'removed' | 'changed';
  code: string;
  title: string;
  // The proposed section (added/changed) or the current one (removed).
  section: ProposalSection;
  fieldChanges: SectionFieldChange[];
};

export type TemplateProposalDiff = {
  isNew: boolean;
  metaChanges: MetaFieldChange[];
  sectionDiffs: TemplateSectionDiff[];
  unchangedSections: number;
};

const sectionFieldValue = (
  section: ProposalSection,
  field: SectionField,
): string | boolean | undefined => section[field];

const sectionFieldEquals = (
  left: ProposalSection,
  right: ProposalSection,
  field: SectionField,
): boolean => {
  if (field === 'required') {
    // An omitted flag means "not required" — same as an explicit false.
    return (left.required ?? false) === (right.required ?? false);
  }
  if (field === 'title' || field === 'instructions') {
    // Free prose compares trimmed: whitespace-only edge differences are not
    // edits, but anything else — casing included — is a change the approver
    // must see.
    const leftValue = String(sectionFieldValue(left, field) ?? '');
    const rightValue = String(sectionFieldValue(right, field) ?? '');
    return leftValue.trim() === rightValue.trim();
  }
  return sameDisplayValue(
    sectionFieldValue(left, field) as string | undefined,
    sectionFieldValue(right, field) as string | undefined,
  );
};

// The client's domain vocabulary is screaming-case (e.g. 'FOLLOW_UP_LETTER')
// while the model's proposal echoes the wire spelling ('follow_up_letter').
// Compare display values case- and separator-insensitively so formatting
// never fakes a change.
const sameDisplayValue = (left?: string, right?: string): boolean =>
  (left ?? '').trim().toLowerCase().replace(/_/g, ' ') ===
  (right ?? '').trim().toLowerCase().replace(/_/g, ' ');

// Free prose compares strictly (trimmed): a rename that only changes casing
// is a real edit the approver must see. Enum-ish fields keep the
// case/separator-insensitive compare so wire spelling never fakes a change.
const metaDiffers = (field: ProposalField, from?: string, to?: string) =>
  field === 'name' || field === 'description'
    ? (from ?? '').trim() !== (to ?? '').trim()
    : !sameDisplayValue(from, to);

export const diffTemplateSections = (
  current: DocumentTemplate | undefined,
  proposal: ParsedTemplateProposal,
): TemplateProposalDiff => {
  const currentSections = current?.sections ?? [];
  const currentByKey = new Map(
    currentSections.map((section) => [
      sectionKey({
        code: section.code,
        title: section.title,
      }),
      section,
    ]),
  );
  const proposedByKey = new Map(
    proposal.sections.map((section) => [sectionKey(section), section]),
  );

  const sectionDiffs: TemplateSectionDiff[] = [];
  let unchangedSections = 0;

  for (const section of proposal.sections) {
    const before = currentByKey.get(sectionKey(section));
    if (!before) {
      sectionDiffs.push({
        status: 'added',
        code: section.code,
        title: section.title,
        section,
        fieldChanges: [],
      });
      continue;
    }
    const beforeNormalized: ProposalSection = {
      code: before.code,
      title: before.title,
      category: before.category,
      instructions: before.instructions,
      templateType: before.template_type,
      processingType: before.processing_type,
      required: before.required,
    };
    const fieldChanges: SectionFieldChange[] = [];
    for (const field of [
      'title',
      'category',
      'instructions',
      'templateType',
      'processingType',
      'required',
    ] as const) {
      if (!sectionFieldEquals(beforeNormalized, section, field)) {
        fieldChanges.push({
          field,
          from: sectionFieldValue(beforeNormalized, field),
          to: sectionFieldValue(section, field),
        });
      }
    }
    if (fieldChanges.length) {
      sectionDiffs.push({
        status: 'changed',
        code: section.code,
        title: section.title,
        section,
        fieldChanges,
      });
    } else {
      unchangedSections += 1;
    }
  }

  for (const section of currentSections) {
    if (
      !proposedByKey.has(
        sectionKey({
          code: section.code,
          title: section.title,
        }),
      )
    ) {
      sectionDiffs.push({
        status: 'removed',
        code: section.code,
        title: section.title,
        section: {
          code: section.code,
          title: section.title,
          category: section.category,
          instructions: section.instructions,
          templateType: section.template_type,
          processingType: section.processing_type,
          required: section.required,
        },
        fieldChanges: [],
      });
    }
  }

  const metaChanges: MetaFieldChange[] = [];
  const metaFields: ProposalField[] = [
    'name',
    'tabTitle',
    'description',
    'type',
    'language',
  ];
  const currentMeta: Record<ProposalField, string | undefined> = {
    name: current?.name ?? undefined,
    tabTitle: current?.tab_title ?? undefined,
    description: current?.description ?? undefined,
    type: current?.type,
    language: current?.language,
  };
  for (const field of metaFields) {
    const to = proposal.meta[field];
    const from = currentMeta[field];
    if (metaDiffers(field, from, to)) {
      metaChanges.push({ field, from: from ?? undefined, to });
    }
  }

  return {
    isNew: !proposal.baseSeriesID,
    metaChanges,
    sectionDiffs,
    unchangedSections,
  };
};

export type SchemaDocumentDiff = {
  status: 'added' | 'removed' | 'changed';
  seriesID: string;
  inclusion?: string;
  previousInclusion?: string;
};

export type SchemaProposalDiff = {
  isNew: boolean;
  metaChanges: MetaFieldChange[];
  documentDiffs: SchemaDocumentDiff[];
};

export const diffSchemaProposal = (
  current: EncounterSchema | undefined,
  proposal: ParsedSchemaProposal,
): SchemaProposalDiff => {
  const currentDocuments = current?.documents ?? [];
  const currentBySeries = new Map(
    currentDocuments.map((document) => [
      document.template_series_id ?? '',
      document,
    ]),
  );
  const proposedBySeries = new Map(
    proposal.documents.map((document) => [document.seriesID, document]),
  );

  const documentDiffs: SchemaDocumentDiff[] = [];
  for (const document of proposal.documents) {
    const before = currentBySeries.get(document.seriesID);
    if (!before) {
      documentDiffs.push({
        status: 'added',
        seriesID: document.seriesID,
        inclusion: document.inclusion,
      });
    } else if (
      (before.inclusion ?? 'always') !== (document.inclusion ?? 'always')
    ) {
      documentDiffs.push({
        status: 'changed',
        seriesID: document.seriesID,
        inclusion: document.inclusion,
        previousInclusion: before.inclusion,
      });
    }
  }
  for (const document of currentDocuments) {
    const seriesID = document.template_series_id ?? '';
    if (!proposedBySeries.has(seriesID)) {
      documentDiffs.push({
        status: 'removed',
        seriesID,
        inclusion: document.inclusion,
      });
    }
  }

  const metaChanges: MetaFieldChange[] = [];
  const currentMeta: Record<ProposalField, string | undefined> = {
    name: current?.name ?? undefined,
    tabTitle: undefined,
    description: current?.description ?? undefined,
    type: undefined,
    language: current?.language,
  };
  for (const field of ['name', 'description', 'language'] as const) {
    const to = proposal.meta[field];
    const from = currentMeta[field];
    if (metaDiffers(field, from, to)) {
      metaChanges.push({ field, from: from ?? undefined, to });
    }
  }

  return {
    isNew: !proposal.schemaID,
    metaChanges,
    documentDiffs,
  };
};
